import { startTransition } from "react";
import { Sidebar } from "@/components/layout/Sidebar";
import type { AppShellNavProps } from "./AppShell";

function closeStandaloneViews(nav: AppShellNavProps) {
  nav.setShowLists(false);
  nav.setShowCoworkers(false);
  nav.setShowAutomations(false);
  nav.setShowSkills(false);
  nav.setShowConnectedTools(false);
  nav.setShowLibrary(false);
  nav.setShowInvites(false);
  nav.setSelectedListId(null);
}

/**
 * The inline Sidebar JSX block from `AppShell.tsx` lifted into a
 * sibling component. Pure render — every input is `nav`. Extracted
 * solely so `AppShell` stays under the 400-line code-locality cap
 * after the mobile-drawer wrapping was added.
 */
export function AppShellSidebar({
  nav,
  inDrawer = false,
}: {
  nav: AppShellNavProps;
  inDrawer?: boolean;
}) {
  return (
    <Sidebar
      inDrawer={inDrawer}
      scope={nav.scope}
      onScopeChange={nav.handleScopeChange}
      onShowInbox={() => {
        nav.setSelectedChannel(null);
        closeStandaloneViews(nav);
      }}
      showInbox={
        !nav.selectedChannel &&
        !nav.showLists &&
        !nav.showCoworkers &&
        !nav.showAutomations &&
        !nav.showSkills &&
        !nav.showConnectedTools &&
        !nav.showLibrary &&
        !nav.showInvites &&
        !nav.selectedListId
      }
      selectedWorkspace={nav.selectedWorkspace}
      onSelectWorkspace={(id: string | null) => {
        if (id) {
          const ws = nav.workspaceList.find((w) => w.id === id);
          if (ws?.slug && nav.navigateToSubdomain(ws.slug)) return;
        }
        nav.handleSelectWorkspace(id);
      }}
      selectedChannel={nav.selectedChannel}
      onSelectChannel={(id: string | null) => {
        if (id) {
          nav.openChannelWithContext(id, {
            workspaceId: nav.selectedWorkspace,
          });
        } else {
          nav.setSelectedChannel(null);
        }
      }}
      openChannelWithContext={nav.openChannelWithContext}
      showLists={nav.showLists || !!nav.selectedListId}
      onToggleLists={nav.handleToggleLists}
      showCoworkers={nav.showCoworkers}
      onToggleCoworkers={nav.handleToggleCoworkers}
      showAutomations={nav.showAutomations}
      onToggleAutomations={nav.handleToggleAutomations}
      showInvites={nav.showInvites}
      onToggleInvites={nav.handleToggleInvites}
      selectedListId={nav.selectedListId}
      onSelectList={(id: string) => {
        startTransition(() => {
          nav.setSelectedListId(id);
          closeStandaloneViews(nav);
        });
      }}
      onCreateListItem={(preselectedListId?: string) => {
        nav.setListItemCreatePreselect(preselectedListId);
        nav.setShowListItemCreate(true);
      }}
      onGoHome={() => {
        if (nav.navigateToSubdomain("home")) return;
        nav.setScope("home");
        nav.setSelectedChannel(null);
        closeStandaloneViews(nav);
      }}
      onMembershipLost={(name) => nav.setMembershipLostWorkspace(name)}
      workspaceList={nav.workspaceList}
      setPendingWorkspace={nav.setPendingWorkspace}
    />
  );
}
